// The free floor area where a floor's pets walk (« La poule de l’étage », chicken.js; « Le chat de
// l’étage », cat.js): a rectangle around the floor's content minus its pods, walkways and hall, and
// the straight-line checks their walks use. Pure geometry, no three.js import (unit-tested in Node).

import { DIMS } from './layout.js';
import { clamp, lerp } from './anim.js';

/**
 * Where a pet may walk on a floor (floor-local, layout.js): a rectangle around the floor's content
 * (grown by `edge`) kept inside its slab, minus every pod, walkway and the hall (with the elevator
 * cabin), each grown by `margin` (half the pet and some room).
 * @param {{bounds:{minX:number,maxX:number,minZ:number,maxZ:number}, slab?:{x:number,z:number,w:number,d:number},
 *   hall?:{r:number}, pods?:Array<{x:number,z:number,w:number,d:number}>,
 *   walkways?:Array<{x:number,z:number,length:number,angle:number}>}} layout
 * @param {{margin?:number, edge?:number, near?:number}} [opts] near: how close to a team its targets stay
 */
export function floorArea(layout, { margin = 0, edge = 0, near = Infinity } = {}) {
  const m = margin;
  const b = layout.bounds;
  let minX = b.minX - edge;
  let maxX = b.maxX + edge;
  let minZ = b.minZ - edge;
  let maxZ = b.maxZ + edge;
  const s = layout.slab;
  if (s) {
    minX = Math.max(minX, s.x - s.w / 2 + m);
    maxX = Math.min(maxX, s.x + s.w / 2 - m);
    minZ = Math.max(minZ, s.z - s.d / 2 + m);
    maxZ = Math.min(maxZ, s.z + s.d / 2 - m);
  }
  const boxes = (layout.pods || []).map((p) => ({
    minX: p.x - p.w / 2 - m, maxX: p.x + p.w / 2 + m, minZ: p.z - p.d / 2 - m, maxZ: p.z + p.d / 2 + m,
  }));
  const circles = [{ x: 0, z: 0, r: (layout.hall?.r ?? DIMS.hallR) + m }];
  // Walkways: rectangles along their angle (local u along the walkway, v across).
  const strips = (layout.walkways || []).map((w) => ({
    x: w.x, z: w.z, c: Math.cos(w.angle), s: Math.sin(w.angle), hl: w.length / 2 + m, hw: DIMS.walkW / 2 + m,
  }));
  return { minX, maxX, minZ, maxZ, boxes, circles, strips, near };
}

/** True when the point is close to a team: a pod, or the hall on a floor without departments. */
export function nearTeam(area, x, z) {
  const near = area.near ?? Infinity;
  if (!area.boxes.length) {
    const c = area.circles[0];
    return !c || Math.hypot(x - c.x, z - c.z) - c.r <= near;
  }
  for (let i = 0; i < area.boxes.length; i++) {
    const b = area.boxes[i];
    const dx = Math.max(b.minX - x, 0, x - b.maxX);
    const dz = Math.max(b.minZ - z, 0, z - b.maxZ);
    if (dx * dx + dz * dz <= near * near) return true;
  }
  return false;
}

function inRect(a, x, z) {
  return x >= a.minX && x <= a.maxX && z >= a.minZ && z <= a.maxZ;
}

/** Segment (x0,z0) + t·(dx,dz), t ∈ [0,1], meets the box (slab clipping). */
function segHitsBox(x0, z0, dx, dz, minX, maxX, minZ, maxZ) {
  let t0 = 0;
  let t1 = 1;
  if (Math.abs(dx) < 1e-12) {
    if (x0 < minX || x0 > maxX) return false;
  } else {
    let a = (minX - x0) / dx;
    let b = (maxX - x0) / dx;
    if (a > b) { const t = a; a = b; b = t; }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return false;
  }
  if (Math.abs(dz) < 1e-12) return z0 >= minZ && z0 <= maxZ;
  let a = (minZ - z0) / dz;
  let b = (maxZ - z0) / dz;
  if (a > b) { const t = a; a = b; b = t; }
  if (a > t0) t0 = a;
  if (b < t1) t1 = b;
  return t0 <= t1;
}

function segHitsCircle(x0, z0, dx, dz, c) {
  const len2 = dx * dx + dz * dz;
  const t = len2 > 1e-12 ? clamp(((c.x - x0) * dx + (c.z - z0) * dz) / len2, 0, 1) : 0;
  const px = x0 + dx * t - c.x;
  const pz = z0 + dz * t - c.z;
  return px * px + pz * pz < c.r * c.r;
}

function segHitsStrip(x0, z0, dx, dz, w) {
  const rx = x0 - w.x;
  const rz = z0 - w.z;
  const u0 = rx * w.c + rz * w.s;
  const v0 = -rx * w.s + rz * w.c;
  const du = dx * w.c + dz * w.s;
  const dv = -dx * w.s + dz * w.c;
  return segHitsBox(u0, v0, du, dv, -w.hl, w.hl, -w.hw, w.hw);
}

/** Distance from the point (px,pz) to the segment (x0,z0) + t·(dx,dz), t ∈ [0,1]. */
export function segmentDistance(x0, z0, dx, dz, px, pz) {
  const len2 = dx * dx + dz * dz;
  const t = len2 > 1e-12 ? clamp(((px - x0) * dx + (pz - z0) * dz) / len2, 0, 1) : 0;
  return Math.hypot(x0 + dx * t - px, z0 + dz * t - pz);
}

/** True when the point is on the free floor area. */
export function isFree(area, x, z) {
  return segmentClear(area, x, z, x, z);
}

/**
 * True when the straight segment stays on the free floor area (inside the rectangle, through no
 * grown obstacle).
 */
export function segmentClear(area, x0, z0, x1, z1) {
  if (!inRect(area, x0, z0) || !inRect(area, x1, z1)) return false;
  const dx = x1 - x0;
  const dz = z1 - z0;
  const { boxes, circles, strips } = area;
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    if (segHitsBox(x0, z0, dx, dz, b.minX, b.maxX, b.minZ, b.maxZ)) return false;
  }
  for (let i = 0; i < circles.length; i++) if (segHitsCircle(x0, z0, dx, dz, circles[i])) return false;
  for (let i = 0; i < strips.length; i++) if (segHitsStrip(x0, z0, dx, dz, strips[i])) return false;
  return true;
}

/**
 * A free point of the area near a team (random; any free point, then a corner of the rectangle,
 * always free, as a last resort).
 */
export function startPoint(area, rand) {
  for (let i = 0; i < 400; i++) {
    const x = lerp(area.minX, area.maxX, rand());
    const z = lerp(area.minZ, area.maxZ, rand());
    if (isFree(area, x, z) && (i < 300 ? nearTeam(area, x, z) : true)) return { x, z };
  }
  return { x: area.maxX - 0.05, z: area.maxZ - 0.05 };
}
